import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { X, MapPin, Truck } from 'lucide-react';

const TABS = [
  { key: 'addresses',       label: 'Hasta Adresleri', icon: MapPin },
  { key: 'transport-types', label: 'Sevk Türleri',    icon: Truck  },
];

const LookupsPage = () => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab]   = useState('addresses');
  const [search, setSearch]         = useState('');
  const [showModal, setShowModal]   = useState(false);
  const [editItem, setEditItem]     = useState(null);

  const isAdmin = ['super_admin', 'admin', 'supervisor'].includes(user?.role);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Adresler / Sevk Türleri
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Hasta adresleri ve sevk türleri veri tabanı yönetimi
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 mb-6 w-fit">
        {TABS.map((tab) => {
          const Icon   = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSearch(''); }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                active
                  ? 'bg-white text-medical-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab İçerikleri */}
      {activeTab === 'addresses' && (
        <AddressesTab
          search={search}
          setSearch={setSearch}
          isAdmin={isAdmin}
          showModal={showModal}
          setShowModal={setShowModal}
          editItem={editItem}
          setEditItem={setEditItem}
        />
      )}
      {activeTab === 'transport-types' && (
        <TransportTypesTab
          search={search}
          setSearch={setSearch}
          isAdmin={isAdmin}
          showModal={showModal}
          setShowModal={setShowModal}
          editItem={editItem}
          setEditItem={setEditItem}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
//  HASTA ADRESLERİ TAB
// ═══════════════════════════════════════════════════════

function AddressesTab({ search, setSearch, isAdmin, showModal, setShowModal, editItem, setEditItem }) {
  const { toast }      = useToast();
  const queryClient    = useQueryClient();

  const { data: resp, isLoading } = useQuery({
    queryKey: ['hospital-addresses'],
    queryFn: () => api.get('/lookups/addresses').then(r => r.data),
  });

  const items = resp?.data || [];

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(a => a.name.toLowerCase().includes(q));
  }, [items, search]);

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/lookups/addresses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['hospital-addresses']);
      toast({ title: 'Başarılı', description: 'Adres silindi' });
    },
    onError: (err) => toast({ title: 'Hata', description: err.response?.data?.message || 'Silinemedi', variant: 'destructive' }),
  });

  return (
    <>
      <TableToolbar
        search={search}
        setSearch={setSearch}
        placeholder="Adres ara..."
        isAdmin={isAdmin}
        onAdd={() => { setEditItem(null); setShowModal(true); }}
        count={filtered.length}
        totalCount={items.length}
      />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-6 py-3 text-left font-semibold text-slate-600 w-16">#</th>
              <th className="px-6 py-3 text-left font-semibold text-slate-600">ADRES ADI</th>
              {isAdmin && <th className="px-6 py-3 text-right font-semibold text-slate-600 w-32">İŞLEMLER</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={isAdmin ? 3 : 2} className="text-center py-12 text-slate-400">Yükleniyor...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={isAdmin ? 3 : 2} className="text-center py-12 text-slate-400">Kayıt bulunamadı</td></tr>
            ) : filtered.map((item, idx) => (
              <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-3 text-slate-400 font-mono text-xs">{idx + 1}</td>
                <td className="px-6 py-3 font-medium text-slate-800">{item.name}</td>
                {isAdmin && (
                  <td className="px-6 py-3 text-right">
                    <div className="flex justify-end space-x-1">
                      <button
                        onClick={() => { setEditItem(item); setShowModal(true); }}
                        className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Düzenle"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`"${item.name}" adresini silmek istediğinize emin misiniz?`)) {
                            deleteMutation.mutate(item.id);
                          }
                        }}
                        className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Sil"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <AddressModal
          item={editItem}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSuccess={() => {
            setShowModal(false);
            setEditItem(null);
            queryClient.invalidateQueries(['hospital-addresses']);
          }}
        />
      )}
    </>
  );
}

function AddressModal({ item, onClose, onSuccess }) {
  const { toast }    = useToast();
  const isEdit       = !!item;
  const [name, setName]       = useState(item?.name || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({ title: 'Hata', description: 'Adres adı zorunludur', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/lookups/addresses/${item.id}`, { name: name.trim() });
      } else {
        await api.post('/lookups/addresses', { name: name.trim() });
      }
      toast({ title: 'Başarılı', description: isEdit ? 'Adres güncellendi' : 'Adres eklendi' });
      onSuccess();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.message || 'İşlem başarısız', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={isEdit ? 'Adresi Düzenle' : 'Yeni Adres Ekle'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>Adres / Hastane Adı *</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Örn: Özel Acıbadem Bakırköy Hastanesi"
            autoFocus
          />
        </div>
        <ModalActions loading={loading} onClose={onClose} submitText={isEdit ? 'Güncelle' : 'Ekle'} />
      </form>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════
//  SEVK TÜRLERİ TAB
// ═══════════════════════════════════════════════════════

function TransportTypesTab({ search, setSearch, isAdmin, showModal, setShowModal, editItem, setEditItem }) {
  const { toast }   = useToast();
  const queryClient = useQueryClient();

  const { data: resp, isLoading } = useQuery({
    queryKey: ['transport-types'],
    queryFn: () => api.get('/lookups/transport-types').then(r => r.data),
  });

  const items = resp?.data || [];

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(t => t.label.toLowerCase().includes(q) || t.value.toLowerCase().includes(q));
  }, [items, search]);

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/lookups/transport-types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['transport-types']);
      toast({ title: 'Başarılı', description: 'Sevk türü silindi' });
    },
    onError: (err) => toast({ title: 'Hata', description: err.response?.data?.message || 'Silinemedi', variant: 'destructive' }),
  });

  return (
    <>
      <TableToolbar
        search={search}
        setSearch={setSearch}
        placeholder="Sevk türü ara..."
        isAdmin={isAdmin}
        onAdd={() => { setEditItem(null); setShowModal(true); }}
        count={filtered.length}
        totalCount={items.length}
      />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-6 py-3 text-left font-semibold text-slate-600 w-16">#</th>
              <th className="px-6 py-3 text-left font-semibold text-slate-600 w-56">KOD</th>
              <th className="px-6 py-3 text-left font-semibold text-slate-600">SEVK TÜRÜ ADI</th>
              {isAdmin && <th className="px-6 py-3 text-right font-semibold text-slate-600 w-32">İŞLEMLER</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={isAdmin ? 4 : 3} className="text-center py-12 text-slate-400">Yükleniyor...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={isAdmin ? 4 : 3} className="text-center py-12 text-slate-400">Kayıt bulunamadı</td></tr>
            ) : filtered.map((item, idx) => (
              <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-3 text-slate-400 font-mono text-xs">{idx + 1}</td>
                <td className="px-6 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 font-mono">
                    {item.value}
                  </span>
                </td>
                <td className="px-6 py-3 font-medium text-slate-800">{item.label}</td>
                {isAdmin && (
                  <td className="px-6 py-3 text-right">
                    <div className="flex justify-end space-x-1">
                      <button
                        onClick={() => { setEditItem(item); setShowModal(true); }}
                        className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Düzenle"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`"${item.label}" sevk türünü silmek istediğinize emin misiniz?`)) {
                            deleteMutation.mutate(item.id);
                          }
                        }}
                        className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Sil"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <TransportTypeModal
          item={editItem}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSuccess={() => {
            setShowModal(false);
            setEditItem(null);
            queryClient.invalidateQueries(['transport-types']);
          }}
        />
      )}
    </>
  );
}

function TransportTypeModal({ item, onClose, onSuccess }) {
  const { toast } = useToast();
  const isEdit    = !!item;
  const [form, setForm]           = useState({ value: item?.value || '', label: item?.label || '' });
  const [loading, setLoading]     = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !form.value.trim()) {
      toast({ title: 'Hata', description: 'Kod zorunludur', variant: 'destructive' });
      return;
    }
    if (!form.label.trim()) {
      toast({ title: 'Hata', description: 'Sevk türü adı zorunludur', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/lookups/transport-types/${item.id}`, { label: form.label.trim() });
      } else {
        await api.post('/lookups/transport-types', { value: form.value.trim(), label: form.label.trim() });
      }
      toast({ title: 'Başarılı', description: isEdit ? 'Sevk türü güncellendi' : 'Sevk türü eklendi' });
      onSuccess();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.message || 'İşlem başarısız', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={isEdit ? 'Sevk Türünü Düzenle' : 'Yeni Sevk Türü Ekle'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {!isEdit && (
          <div className="space-y-1.5">
            <Label>Kod *</Label>
            <Input
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
              placeholder="Örn: YENI_SEVK_TURU"
              className="font-mono"
              autoFocus
            />
            <p className="text-xs text-slate-400">Büyük harf ve alt çizgi kullanın. Boşluklar otomatik _ olur.</p>
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Sevk Türü Adı *</Label>
          <Input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Örn: Yeni Sevk Türü"
            autoFocus={isEdit}
          />
        </div>
        <ModalActions loading={loading} onClose={onClose} submitText={isEdit ? 'Güncelle' : 'Ekle'} />
      </form>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════
//  ORTAK BİLEŞENLER (MedicalDefinitionsPage ile aynı pattern)
// ═══════════════════════════════════════════════════════

function TableToolbar({ search, setSearch, placeholder, isAdmin, onAdd, count, totalCount }) {
  return (
    <div className="flex items-center justify-between mb-4 gap-4">
      <div className="flex items-center gap-3 flex-1">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={placeholder}
            className="pl-9 h-10"
          />
        </div>
        {totalCount > 0 && (
          <span className="text-sm text-slate-400 whitespace-nowrap">
            {search ? `${count} / ${totalCount}` : `${totalCount} kayıt`}
          </span>
        )}
      </div>
      {isAdmin && (
        <Button onClick={onAdd} className="bg-medical-600 hover:bg-medical-700 shadow-sm">
          <PlusIcon className="h-4 w-4 mr-2" />
          Yeni Ekle
        </Button>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ loading, onClose, submitText }) {
  return (
    <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
      <Button type="button" variant="outline" onClick={onClose} className="h-10">
        İptal
      </Button>
      <Button type="submit" disabled={loading} className="h-10 bg-medical-600 hover:bg-medical-700 shadow-sm">
        {loading ? 'Kaydediliyor...' : submitText}
      </Button>
    </div>
  );
}

export default LookupsPage;
